import { useTranslation } from 'react-i18next';
import { Email, PhoneCall, Whatsapp } from '@insify/design-system/line-icons';
import { COUNTRY } from '~globals';
import { BaseLink } from '~components/BaseLink';

import * as styles from './Footer.module.css';

import type { TFunction } from 'i18next';
import type { Country } from '~models/country';

type ContactOption = (t: TFunction) => {
  Icon: React.FC<React.SVGProps<SVGSVGElement>>;
  title: string;
  subtitle: string;
  link: { id: string; label: string; url: string };
};

type ContactOptions = { [Key in Country]: ContactOption[] };

const whatsappURLByLocale: Record<Country, string> = {
  de: 'https://wa.me/496994323111',
  nl: 'https://wa.me/31208881089',
  fr: 'https://wa.me/33186472770',
};

const whatsapp: ContactOption = (t: TFunction) => ({
  Icon: Whatsapp,
  title: t('common:footer_contact_whatsapp_title'),
  subtitle: t('common:footer_contact_whatsapp_subtitle'),
  link: {
    id: 'whatsapp',
    label: t('common:footer_contact_whatsapp_cta'),
    url: whatsappURLByLocale[COUNTRY],
  },
});

const phone: ContactOption = (t: TFunction) => ({
  Icon: PhoneCall,
  title: t('common:footer_contact_phone_title'),
  subtitle: t('common:footer_contact_phone_subtitle'),
  link: {
    id: 'phone',
    label: t('common:footer_contact_phone_cta'),
    url: `tel:${t('common:footer_contact_header_phone_number')}`,
  },
});

const email: ContactOption = (t: TFunction) => ({
  Icon: Email,
  title: t('common:footer_contact_email_title'),
  subtitle: t('common:footer_contact_email_subtitle'),
  link: {
    id: 'email',
    label: t('common:footer_contact_email_cta'),
    url: `mailto:${t('common:footer_contact_header_email_address')}`,
  },
});

const contact: ContactOptions = {
  nl: [whatsapp, phone, email],
  de: [whatsapp, email],
  fr: [whatsapp, phone, email],
};

export const useContact = () => {
  const { t } = useTranslation(['common']);
  return {
    title: t('common:footer_contact_header_title'),
    items: contact[COUNTRY].map((contactOption) => contactOption(t)),
  };
};
